// What the Browse search box searches.
//
// Shared because both halves need the same list: the server-rendered dropdown
// (`views/pages/Front.tsx`), the browser code that puts the choice in the payload
// (`features/navigation.ts`) and the query builder that acts on it
// (`trpc/routes/search/load`).
// ⚠️ These are ids, not labels, and the id also spells the URL segment — `artist-name`
// rides in a Browse URL as `mode-artist-name`. Renaming one renames shared links, which
// is why `LEGACY_SEARCH_MODES` below exists.
export const SEARCH_MODES = ["free", "artist-name", "title", "location"];
/** The default, and the behaviour that existed before the dropdown: image embeddings. */
export const DEFAULT_SEARCH_MODE = "free";
export const SEARCH_MODE_LABELS = {
    free: "Free text",
    "artist-name": "Artist name",
    title: "Artwork title",
    location: "Location",
};
/** Placeholder per mode — "drop an image" is only true of the free-text one. */
export const SEARCH_MODE_PLACEHOLDERS = {
    free: "Type or drop an image to search",
    "artist-name": "Search by artist name",
    title: "Search in artwork titles",
    location: "Search by place, city or country",
};
/**
 * Ids this mode used to have, still accepted wherever a current one is.
 *
 * `photographer` was the id until 2026-10-05, when the dropdown label became "Artist name"
 * and the id followed so a shared link would read the same as the box. Links carrying the
 * old segment keep working — a URL is somebody else's property once they have copied it,
 * and the cost of honouring it forever is this map.
 */
export const LEGACY_SEARCH_MODES = {
    photographer: "artist-name",
};
export function isSearchMode(value) {
    return SEARCH_MODES.includes(value);
}
/** Narrow an untrusted value to a mode, falling back to today's behaviour. */
export function toSearchMode(value) {
    if (!value)
        return DEFAULT_SEARCH_MODE;
    if (isSearchMode(value))
        return value;
    return LEGACY_SEARCH_MODES[value] ?? DEFAULT_SEARCH_MODE;
}
/**
 * The URL path segment that carries a non-default mode.
 *
 * Browse URLs are `/search/<query>/<segment>/<segment>…`, where each segment is a filter
 * option id — so the mode rides in the same way every other filter already does, and a
 * copied link reopens the same view. The default mode emits nothing, which keeps an
 * ordinary free-text URL byte-identical to what it was before the dropdown existed.
 *
 * `mode-` cannot collide with a filter option id: those are `sort-*`, `artist-country-*`,
 * colour names and category slugs, none of which start with it.
 */
export function searchModeSegment(mode) {
    return mode === DEFAULT_SEARCH_MODE ? "" : `mode-${mode}`;
}
/**
 * The mode a Browse URL's path segments ask for.
 *
 * Legacy segments are read as well as current ones, so a link sent before a rename still
 * opens the view it promised. Only the current spelling is ever WRITTEN
 * (`searchModeSegment`), so the old one fades from circulation on its own.
 */
export function searchModeFromSegments(segments) {
    const wanted = segments.map((s) => s.trim());
    for (const m of SEARCH_MODES) {
        if (m !== DEFAULT_SEARCH_MODE && wanted.includes(`mode-${m}`))
            return m;
    }
    for (const [legacy, mode] of Object.entries(LEGACY_SEARCH_MODES)) {
        if (wanted.includes(`mode-${legacy}`))
            return mode;
    }
    return DEFAULT_SEARCH_MODE;
}
//# sourceMappingURL=searchMode.js.map